import {
  Apps as DashboardIcon,
  AssignmentInd as ContactsIcon,
  Category as CategoryIcon,
  PermPhoneMsg as PermPhoneMsgIcon,
  Web as WebIcon,
} from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
} from "react-router-dom";
import { RestrictedByCapability } from "../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermission } from "../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../auth/useUserData.js";
import { useBubbleContext } from "../commons/hooks/useBubbleContext.js";
import { type IMainMenuItem, MainMenu } from "../commons/MainMenu.js";
import { PageNotFound } from "../commons/PageNotFound.js";
import { BuildingIcon } from "../features/buildings/BuildingIcon.js";
import { DocumentIcon } from "../features/documents/DocumentIcon.js";
import { OpportunityIcon } from "../features/opportunities/OpportunityIcon.js";
import { OrganisationAvatar } from "../features/organisations/OrganisationAvatar.js";
import { PlanningIcon } from "../features/planning/PlanningIcon.js";
import { ProjectIcon } from "../features/projects/ProjectIcon.js";
import { useProjectIsOpportunity } from "../features/projects/useProjectIsOpportunity.js";
import { TaskIcon } from "../features/tasks/TaskIcon.js";
import { TemplateIcon } from "../features/templates/TemplateIcon.js";
import { UserAvatar } from "../features/users/UserAvatar.js";
import { BuildingsRoutes } from "./buildings/BuildingsRoutes.js";
import { Calendar } from "./calendar/Calendar.js";
import { CrmRoutes } from "./crm/CrmRoutes.js";
import { Dashboard } from "./dashboard/Dashboard.js";
import { DocumentsRoutes } from "./documents/DocumentsRoutes.js";
import { ExamplesRoutes } from "./examples/ExamplesRoutes.js";
import { OrganisationRoutes } from "./my-organisation/OrganisationRoutes.js";
import { UserRoutes } from "./my-user/UserRoutes.js";
import { OpportunitiesRoutes } from "./opportunities/OpportunitiesRoutes.js";
import { PlanningRoutes } from "./planning/PlanningRoutes.js";
import { ProductsRoutes } from "./products/ProductsRoutes.js";
import { ProjectsRoutes } from "./projects/ProjectsRoutes.js";
import { Schedule } from "./schedule/Schedule.js";
import { TasksRoutes } from "./tasks/TaskRoutes.js";
import { TemplatesRoutes } from "./templates/TemplatesRoutes.js";
import { CallAssistant } from "./call-assistant/CallAssistant.js";
import { WebShopCms } from "./webshopcms/WebShopCms.js";
import { getUrlSearchParamsByParamsName } from "../commons/hooks/useStateWithUrlParams.js";

const DASHBOARD_PATH = "/dashboard";

export type MainMenuItem =
  | { name: "dashboard" }
  | { name: "buildings" }
  | { name: "opportunity" }
  | { name: "project" }
  | { name: "project2" }
  | { name: "documents" }
  | { name: "planning" }
  | { name: "tasks" }
  | { name: "CRM" }
  | { name: "templates" }
  | { name: "products" }
  | { name: "callassistant" }
  | { name: "webshopcms" }
  | { name: "myprofile" }
  | { name: "myprofile-user" }
  | { name: "myprofile-organisation" }
  | { name: "shop" }
  | { name: "agenda" }
  | { name: "meistersystemsadmin" }
  | { name: "organizations" }
  | { name: "login" }
  | { name: "signup" }
  | { name: "__otherprofile" };

type AppSidebarItem = MainMenuItem & Omit<IMainMenuItem, "name">;

export const MainRoutes = () => {
  const location = useLocation();
  const { primaryItems, secondaryItems } = useMainMenuItems();

  return (
    <>
      <MainMenu primaryItems={primaryItems} secondaryItems={secondaryItems} />
      <Routes>
        <Route
          path={"/"}
          element={
            <Navigate
              to={{
                pathname: DASHBOARD_PATH,
                search: location.search,
              }}
              replace
            />
          }
        />
        <Route
          path={"/next"}
          element={
            <Navigate
              to={{
                pathname: DASHBOARD_PATH,
                search: location.search,
              }}
              replace
            />
          }
        />

        <Route path={DASHBOARD_PATH} element={<Dashboard />} />
        <Route path={"/buildings/*"} element={<BuildingsRoutes />} />
        <Route
          path={`/opportunities/*`}
          element={
            <RestrictedByOrganisationPermission permission="CREATE_PROJECT">
              <OpportunitiesRoutes />
            </RestrictedByOrganisationPermission>
          }
        />
        <Route path={`/projects/*`} element={<ProjectsRoutes />} />
        <Route path={`/documents/*`} element={<DocumentsRoutes />} />
        <Route path={`/tasks/*`} element={<TasksRoutes />} />
        <Route
          path={`/planning/*`}
          element={
            <RestrictedByCapability capability="PLANNING">
              <PlanningRoutes />
            </RestrictedByCapability>
          }
        />
        <Route
          path={"/crm/*"}
          element={
            <RestrictedByOrganisationPermission permission="READ_CRM">
              <CrmRoutes />
            </RestrictedByOrganisationPermission>
          }
        />
        <Route
          path={`/templates/*`}
          element={
            <RestrictedByCapability capability="TEMPLATING">
              <RestrictedByOrganisationPermission permission="READ_TEMPLATE">
                <TemplatesRoutes />
              </RestrictedByOrganisationPermission>
            </RestrictedByCapability>
          }
        />
        <Route
          path={"/products/*"}
          element={
            <RestrictedByCapability capability="PIM">
              <ProductsRoutes />
            </RestrictedByCapability>
          }
        />

        <Route
          path={"/callassistant/*"}
          element={
            <RestrictedByCapability capability="CALL_ASSISTANT">
              <CallAssistant />
            </RestrictedByCapability>
          }
        />
        <Route
          path={"/webshopcms/*"}
          element={
            <RestrictedByCapability capability="WEBSHOPCMS">
              <WebShopCms />
            </RestrictedByCapability>
          }
        />

        <Route path={"/organisation/*"} element={<OrganisationRoutes />} />
        <Route path={"/user/*"} element={<UserRoutes />} />

        <Route path={`/schedule`} element={<Schedule />} />
        <Route path={`/calendar`} element={<Calendar />} />

        <Route path={`/examples/*`} element={<ExamplesRoutes />} />

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
};

function useMainMenuItems() {
  const viewer = useUserData().currentUser;
  const { bubble } = useBubbleContext();
  const { t } = useTranslate("SidebarItems");

  const projectMatch = useMatch(`/projects/:projectId/*`);
  const projectId = projectMatch?.params?.projectId;

  const { projectLoading, projectIsOpportunity } =
    useProjectIsOpportunity(projectId);

  const menuItems = React.useMemo(() => {
    if (!viewer) return { primaryItems: [], secondaryItems: [] };

    const items: (AppSidebarItem & { isHidden?: boolean })[] = [
      {
        name: "dashboard",
        label: t("Dashboard"),
        icon: <DashboardIcon />,
        path: "/dashboard",
      },
      {
        name: "buildings",
        label: t("Buildings"),
        icon: <BuildingIcon />,
        path: "/buildings",
        capability: "BUILDINGS",
      },
      {
        name: "opportunity",
        label: t("Opportunities"),
        icon: <OpportunityIcon />,
        path: "/opportunities",
        permission: "CREATE_PROJECT",
        bubbleAmount: 0, //bubble.opportunitiesTotal,
        isActive: match => {
          return Boolean(match) || (!projectLoading && projectIsOpportunity);
        },
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "project",
        label: t("Projects"),
        icon: <ProjectIcon />,
        path: "/projects",
        bubbleAmount: bubble.projectsTotal,
        isActive: (match, location) =>
          Boolean(match) && !projectLoading && !projectIsOpportunity,
      },
      {
        name: "documents",
        label: t("Documents"),
        icon: <DocumentIcon />,
        path: "/documents",
      },
      {
        name: "planning",
        label: t("Planning and Resources"),
        icon: <PlanningIcon />,
        path: "/planning",
        capability: "PLANNING",
      },
      {
        name: "tasks",
        label: t("Tasks"),
        icon: <TaskIcon />,
        path: {
          pathname: "/tasks",
          search: getUrlSearchParamsByParamsName(
            { status: ["OPEN"] },
            "filters"
          ).toString(),
        },
      },
      {
        name: "CRM",
        label: t("CRM"),
        icon: <ContactsIcon />,
        path: "/crm",
        permission: "READ_CRM",
      },
      {
        name: "templates",
        label: t("Templates"),
        icon: <TemplateIcon />,
        path: "/templates",
        capability: "TEMPLATING",
        permission: "READ_TEMPLATE",
      },
      {
        name: "products",
        label: t("Product catalogues"),
        icon: <CategoryIcon />,
        path: "/products",
        capability: "PIM",
      },
      {
        name: "callassistant",
        label: t("Call-Assistant"),
        icon: <PermPhoneMsgIcon />,
        path: "/callassistant",
        capability: "CALL_ASSISTANT",
      },
      {
        name: "webshopcms",
        label: t("Web Shop CMS"),
        icon: <WebIcon />,
        path: "/webshopcms",
        capability: "WEBSHOPCMS",
      },
    ] as const;

    const secondaryItems: (AppSidebarItem & { isHidden?: boolean })[] = [
      {
        name: "myprofile-organisation",
        path: "/organisation",
        label: viewer.organisation.title,
        icon: (
          <OrganisationAvatar
            organisationAvatar={viewer.organisation}
            size="s"
            style={{ left: -4, pointerEvents: "none" }}
          />
        ),
      },
      {
        name: "myprofile-user",
        path: "/user",
        label: `${viewer.firstname} ${viewer.familyname}`,
        icon: (
          <UserAvatar
            userAvatar={viewer}
            size="s"
            style={{ left: -4, pointerEvents: "none" }}
          />
        ),
      },
    ];

    return {
      primaryItems: items.filter(item => item.isHidden !== true),
      secondaryItems,
    };
  }, [bubble.projectsTotal, t, viewer, projectLoading, projectIsOpportunity]);

  return menuItems;
}
